import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Copy, ExternalLink, AlertTriangle, Loader2 } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CASA_PRONTA_URL } from "@/lib/casaProntaBookmarklet";
import { CasaProntaFavorito } from "@/components/CasaProntaDefinicoes";

const opt = "text-xs px-3 py-1.5 rounded-lg border transition-colors";

export function CasaPronta({ pid }) {
  const [d, setD] = useState(null);
  const [missing, setMissing] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/processes/${pid}/casa-pronta`).then(({ data }) => setD(data));
  useEffect(() => { load(); }, [pid]);

  const save = async (body) => {
    try { await api.put(`/processes/${pid}/casa-pronta`, body); setMissing(null); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const copy = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/processes/${pid}/casa-pronta`);
      setD(data);
      if (data.missing.length) { setMissing(data.missing); return; }
      await navigator.clipboard.writeText(data.prefix + JSON.stringify(data.payload));
      setMissing(null);
      api.post(`/processes/${pid}/casa-pronta/copied`);
      toast.success("Copiado. Abra o Casa Pronta e carregue no favorito «Preencher ImoDesk».");
    } catch (e) {
      toast.error(e.response ? apiErr(e.response.data?.detail) : "Não foi possível copiar. Autorize o acesso à área de transferência.");
    } finally { setBusy(false); }
  };

  if (!d) return null;
  const destino = d.cp_destino || d.destino_caderneta || "";
  return (
    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50/60 p-3 space-y-3" data-testid="casa-pronta-panel">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        <div className="flex items-center gap-2 text-xs">
          <span className="text-slate-600">Está arrendado?</span>
          {[["sim", "Sim"], ["nao", "Não"]].map(([v, l]) => (
            <button key={v} type="button" onClick={() => save({ rented_answer: v })} data-testid={`cp-rented-${v}`}
              className={`${opt} ${d.rented_answer === v ? "bg-ec-green text-white border-ec-green" : "bg-white border-slate-200 hover:bg-slate-50"}`}>{l}</button>
          ))}
          {!d.rented_answer && <span className="text-amber-700">por responder</span>}
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-slate-600">Destino</span>
          <Select value={destino} onValueChange={(v) => save({ cp_destino: v })}>
            <SelectTrigger className="h-8 w-40 bg-white text-xs" data-testid="cp-destino"><SelectValue placeholder="Escolher" /></SelectTrigger>
            <SelectContent>{d.destinos.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
          </Select>
          {!d.cp_destino && d.destino_caderneta && <span className="text-slate-400">da caderneta</span>}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={copy} disabled={busy} data-testid="cp-copy-btn"
          className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg bg-ec-green text-white hover:bg-ec-greenDark transition-colors disabled:opacity-60">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Copy size={13} />} Copiar para o Casa Pronta
        </button>
        <a href={CASA_PRONTA_URL} target="_blank" rel="noreferrer" data-testid="cp-open-link"
          className="inline-flex items-center gap-1 text-xs text-ec-green hover:underline">Abrir o Casa Pronta <ExternalLink size={12} /></a>
      </div>
      {missing && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-800" data-testid="cp-missing">
          <div className="flex items-center gap-1.5 font-semibold mb-1"><AlertTriangle size={13} /> Não copiado. Falta:</div>
          <ul className="list-disc pl-5 space-y-0.5">{missing.map((m) => <li key={m}>{m}</li>)}</ul>
        </div>
      )}
      <CasaProntaFavorito />
    </div>
  );
}
